import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Overlay, ProgressBar, Tooltip } from 'react-bootstrap';

const getStepState = (steps, index) => {
	const step = steps[index];
	const prevDone = index === 0 || steps.slice(0, index).every((item) => item.done);
	if (step.done) return 'done';
	if (prevDone) return 'current';
	return 'pending';
};

const HoverableStepTooltip = ({ tooltipId, tooltipContent, children }) => {
	const [showTooltip, setShowTooltip] = useState(false);
	const [overlayContainer, setOverlayContainer] = useState(null);
	const triggerRef = useRef(null);
	const hideTimerRef = useRef(null);

	const clearHideTimer = () => {
		if (!hideTimerRef.current) return;
		clearTimeout(hideTimerRef.current);
		hideTimerRef.current = null;
	};

	const handleShow = () => {
		clearHideTimer();
		const el = triggerRef.current;
		if (el) {
			// 详情页没有 .modal-content；挂在图标盒子上时 Popper 会把宽度压成图标宽，中文被挤成竖排
			setOverlayContainer(el.closest('.modal-content') || document.body);
		}
		setShowTooltip(true);
	};

	const handleHideWithDelay = () => {
		clearHideTimer();
		hideTimerRef.current = setTimeout(() => setShowTooltip(false), 220);
	};

	useEffect(() => () => clearHideTimer(), []);

	return (
		<div className='d-inline-flex position-relative'>
			<div ref={triggerRef} className='d-inline-flex' onMouseEnter={handleShow} onMouseLeave={handleHideWithDelay} onFocus={handleShow} onBlur={handleHideWithDelay}>
				{children}
			</div>
			<Overlay target={triggerRef.current} container={overlayContainer} show={showTooltip} placement='bottom'>
				{(overlayProps) => (
					<Tooltip id={tooltipId} {...overlayProps} style={{ ...overlayProps.style, pointerEvents: 'auto' }} onMouseEnter={handleShow} onMouseLeave={handleHideWithDelay}>
						{tooltipContent}
					</Tooltip>
				)}
			</Overlay>
		</div>
	);
};

const renderTooltipContent = (step) => {
	if (!Array.isArray(step.tooltipColumns) || step.tooltipColumns.length === 0) {
		return `${step.label}：${step.done ? step.doneText || '已完成' : step.pendingText || step.badgeText || '未完成'}`;
	}

	return (
		<div className='d-inline-flex align-items-stretch text-nowrap'>
			{step.tooltipColumns.map((item, columnIndex) => (
				<div
					key={`${item.value}-${columnIndex}`}
					className='text-start px-2'
					style={columnIndex < step.tooltipColumns.length - 1 ? { borderRight: '1px solid rgba(255, 255, 255, 0.45)' } : undefined}
				>
					<div className='fw-semibold' style={{ fontSize: '0.72rem' }}>
						{item.value}
					</div>
				</div>
			))}
		</div>
	);
};

const MODAL_HEADER_NODE_SIZE = 24;
const COMPACT_NODE_SIZE = 22;
const MODAL_HEADER_TRACK_HEIGHT = 26;
const COMPACT_TRACK_HEIGHT = 26;
const LABEL_HEIGHT = 18;

const getNodeClassName = (state, { header, isDark, useCelebrationTone }) => {
	const base = 'rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 border';

	if (useCelebrationTone) {
		if (state === 'done') return classNames(base, 'bg-white bg-opacity-25 border-white border-opacity-50 text-white');
		if (state === 'current') return classNames(base, 'bg-white text-primary border-white border-2');
		return classNames(base, 'bg-white bg-opacity-10 border-white border-opacity-25 text-white text-opacity-50');
	}

	if (header && isDark) {
		if (state === 'done') return classNames(base, 'bg-primary bg-opacity-25 border-primary border-opacity-50 text-white');
		if (state === 'current') return classNames(base, 'bg-primary text-white border-primary border-2');
		return classNames(base, 'bg-white bg-opacity-10 border-white border-opacity-25 text-white text-opacity-50');
	}

	if (state === 'done') return classNames(base, 'bg-primary bg-opacity-10 border-primary border-opacity-25 text-primary');
	if (state === 'current') return classNames(base, 'bg-primary text-white border-primary border-2');
	return classNames(base, 'bg-200 text-500 border-300');
};

const getLabelClassName = (state, { header, isDark, useCelebrationTone }, labelVariant) => {
	const base = classNames(
		'w-100 text-center text-truncate px-1',
		labelVariant === 'header' ? 'mt-2 fs-11' : 'mt-1',
		labelVariant === 'compact' && 'fs-11',
	);

	if (useCelebrationTone || (header && isDark)) {
		if (state === 'current') return classNames(base, 'text-white fw-semibold');
		if (state === 'done') return classNames(base, 'text-white text-opacity-75');
		return classNames(base, 'text-white text-opacity-50');
	}

	if (state === 'current') return classNames(base, 'text-primary fw-semibold');
	if (state === 'done') return classNames(base, 'text-600');
	return classNames(base, 'text-400');
};

export const OrderHorizontalProgress = ({ steps, variant = 'modalHeader', onCelebration = false, isDark = false, progressCountMode = 'done' }) => {
	const isModalHeader = variant === 'modalHeader';
	const isCompact = variant === 'compact';
	const useCelebrationTone = onCelebration && isDark;
	const doneCount = steps.filter((step) => step.done).length;
	const hasCurrentStep = steps.some((step, index) => getStepState(steps, index) === 'current');
	const displayCount = progressCountMode === 'active' && hasCurrentStep ? doneCount + 1 : doneCount;
	const percent = steps.length ? Math.round((displayCount / steps.length) * 100) : 0;
	const stepStateByKey = Object.fromEntries(steps.map((step, index) => [step.key, getStepState(steps, index)]));

	const trackBorderClass = useCelebrationTone ? 'border-white border-opacity-50' : isDark ? 'border-white border-opacity-25' : 'border-300';
	const doneBorderClass = useCelebrationTone ? 'border-white border-opacity-75' : isDark ? 'border-primary border-opacity-75' : 'border-primary border-opacity-50';
	const toneContext = { header: isModalHeader, isDark, useCelebrationTone };

	const renderStepLabel = (step, state, labelVariant) => {
		const labelText = step.key === 'logistics' ? step.badgeText || step.pendingText || step.shortLabel || step.label : step.shortLabel || step.label;
		return (
			<div
				className={getLabelClassName(state, { ...toneContext, header: labelVariant === 'header' }, labelVariant)}
				style={labelVariant === 'compact' ? { height: LABEL_HEIGHT, lineHeight: `${LABEL_HEIGHT}px` } : { minHeight: LABEL_HEIGHT, lineHeight: 1.2 }}
			>
				{labelText}
			</div>
		);
	};

	const renderStepIcon = (step, index, { header = false, size } = {}) => {
		const state = stepStateByKey[step.key] ?? 'pending';
		const nodeSize = size ?? (header ? MODAL_HEADER_NODE_SIZE : COMPACT_NODE_SIZE);
		const tooltipId = `order-progress-step-${header ? 'header' : isCompact ? 'compact' : 'panel'}-${step.key}-${index}`;
		const tooltipContent = renderTooltipContent(step);

		return (
			<HoverableStepTooltip tooltipId={tooltipId} tooltipContent={tooltipContent}>
				<div
					className={getNodeClassName(state, { header, isDark, useCelebrationTone })}
					style={{ width: nodeSize, height: nodeSize, fontSize: Math.round(nodeSize * 0.42) }}
				>
					<FontAwesomeIcon icon={step.icon} />
				</div>
			</HoverableStepTooltip>
		);
	};

	const renderStepTrack = (step, index, { nodeSize, trackHeight, connectorGap, header = false }) => (
		<div key={step.key} className='min-w-0 d-flex flex-column align-items-center'>
			<div className='position-relative d-flex justify-content-center align-items-center w-100 flex-shrink-0' style={{ height: trackHeight }}>
				{index > 0 ? (
					<span
						aria-hidden
						className={classNames('position-absolute border-top', steps[index - 1].done ? doneBorderClass : trackBorderClass)}
						style={{ left: 0, right: `calc(50% + ${connectorGap}px)`, top: '50%', zIndex: 0, borderTopWidth: header ? 2 : 1.5 }}
					/>
				) : null}
				<div className='position-relative flex-shrink-0' style={{ zIndex: 1 }}>
					{renderStepIcon(step, index, { header, size: nodeSize })}
				</div>
				{index < steps.length - 1 ? (
					<span
						aria-hidden
						className={classNames('position-absolute border-top', step.done ? doneBorderClass : trackBorderClass)}
						style={{ left: `calc(50% + ${connectorGap}px)`, right: 0, top: '50%', zIndex: 0, borderTopWidth: header ? 2 : 1.5 }}
					/>
				) : null}
			</div>
			{renderStepLabel(step, stepStateByKey[step.key] ?? 'pending', header ? 'header' : 'compact')}
		</div>
	);

	if (isModalHeader) {
		const connectorGap = Math.ceil(MODAL_HEADER_NODE_SIZE / 2) + 5;
		return (
			<div className='d-flex align-items-center gap-2 w-100' role='group' aria-label={`完成进度 ${percent}%`}>
				<div className='flex-grow-1 min-w-0' style={{ display: 'grid', gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
					{steps.map((step, index) => renderStepTrack(step, index, { nodeSize: MODAL_HEADER_NODE_SIZE, trackHeight: MODAL_HEADER_TRACK_HEIGHT, connectorGap, header: true }))}
				</div>
				<div className='d-flex align-items-center gap-1 flex-shrink-0' style={{ width: '7rem' }}>
					<ProgressBar
						className={classNames('flex-grow-1 rounded-pill mb-0', useCelebrationTone ? 'bg-white bg-opacity-25' : isDark ? 'bg-white bg-opacity-10' : 'bg-200')}
						style={{ height: 5, transition: 'none' }}
						variant={percent === 100 ? 'success' : 'primary'}
						now={percent}
					/>
					<span
						className={classNames('text-nowrap lh-1 fw-semibold fs-11', useCelebrationTone ? 'text-white text-opacity-90' : isDark ? 'text-300' : 'text-600')}
					>
						{doneCount}/{steps.length}
					</span>
				</div>
			</div>
		);
	}

	const nodeSize = COMPACT_NODE_SIZE;
	const connectorGap = Math.ceil(nodeSize / 2) + 4;
	return (
		<div role='group' aria-label={`流程进度 ${displayCount}/${steps.length}`}>
			<div
				className='w-100'
				style={{ display: 'grid', gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`, columnGap: 2, minHeight: COMPACT_TRACK_HEIGHT + LABEL_HEIGHT + 4 }}
			>
				{steps.map((step, index) => renderStepTrack(step, index, { nodeSize, trackHeight: COMPACT_TRACK_HEIGHT, connectorGap, header: false }))}
			</div>
			<div className='d-flex align-items-center gap-2 mt-2 pt-1' style={{ minHeight: 14 }}>
				<ProgressBar
					className='flex-grow-1 rounded-pill mb-0 bg-200'
					style={{ height: 3, transition: 'none' }}
					variant={percent === 100 ? 'success' : 'primary'}
					now={percent}
					aria-label={`进度 ${percent}%`}
				/>
				<span className='text-400 text-nowrap lh-1 fs-11'>{displayCount}/{steps.length}</span>
			</div>
		</div>
	);
};

export default OrderHorizontalProgress;
